import { MessageSource } from './enums'
import { isWindow } from './type-guards'

export class MessageSourceRegistry {
  private readonly validSources = new WeakSet<Window>()
  private buttonSource: Window | null = null
  private chatSource: Window | null = null
  private greetingSource: Window | null = null
  private readonly expectedOrigin: string | null

  constructor(expectedOrigin: string | null) {
    this.expectedOrigin = expectedOrigin
  }

  registerButtonSource(source: Window | null): boolean {
    if (!source) {
      return false
    }

    try {
      if (typeof source.postMessage !== 'function') {
        console.warn('[MessageSourceRegistry] Invalid button source: not a Window object')
        return false
      }

      if (this.buttonSource) {
        try {
          this.validSources.delete(this.buttonSource)
        } catch {
          // Ignore errors
        }
      }

      this.buttonSource = source
      this.validSources.add(source)
      return true
    } catch (error) {
      console.error('[MessageSourceRegistry] Error registering button source:', error)
      return false
    }
  }

  registerChatSource(source: Window | null): boolean {
    if (!source) {
      return false
    }

    try {
      if (typeof source.postMessage !== 'function') {
        console.warn('[MessageSourceRegistry] Invalid chat source: not a Window object')
        return false
      }

      if (this.chatSource) {
        try {
          this.validSources.delete(this.chatSource)
        } catch {
          // Ignore errors
        }
      }

      this.chatSource = source
      this.validSources.add(source)
      return true
    } catch (error) {
      console.error('[MessageSourceRegistry] Error registering chat source:', error)
      return false
    }
  }

  registerGreetingSource(source: Window | null): boolean {
    if (!source) {
      return false
    }

    try {
      if (typeof source.postMessage !== 'function') {
        console.warn('[MessageSourceRegistry] Invalid greeting source: not a Window object')
        return false
      }

      if (this.greetingSource) {
        try {
          this.validSources.delete(this.greetingSource)
        } catch {
          // Ignore errors
        }
      }

      this.greetingSource = source
      this.validSources.add(source)
      return true
    } catch (error) {
      console.error('[MessageSourceRegistry] Error registering greeting source:', error)
      return false
    }
  }

  getSourceType(event: MessageEvent): MessageSource | null {
    try {
      if (!isWindow(event.source)) {
        return null
      }

      if (this.expectedOrigin && event.origin !== this.expectedOrigin) {
        return null
      }

      let isValidSource = false
      try {
        isValidSource = this.validSources.has(event.source)
      } catch (error) {
        console.warn('[MessageSourceRegistry] Error checking source in WeakSet:', error)
        return null
      }

      if (!isValidSource) {
        return null
      }

      if (event.source === this.buttonSource) {
        return MessageSource.BUTTON
      }
      if (event.source === this.chatSource) {
        return MessageSource.CHAT
      }
      if (event.source === this.greetingSource) {
        return MessageSource.GREETING
      }

      return null
    } catch (error) {
      console.warn('[MessageSourceRegistry] Error validating source:', error)
      return null
    }
  }

  updateButtonSource(source: Window | null): void {
    this.registerButtonSource(source)
  }

  updateChatSource(source: Window | null): void {
    this.registerChatSource(source)
  }

  clearChatSource(): void {
    try {
      if (this.chatSource) {
        this.validSources.delete(this.chatSource)
        this.chatSource = null
      }
    } catch (error) {
      console.warn('[MessageSourceRegistry] Error clearing chat source:', error)
    }
  }

  clear(): void {
    try {
      if (this.buttonSource) {
        this.validSources.delete(this.buttonSource)
        this.buttonSource = null
      }
      if (this.chatSource) {
        this.validSources.delete(this.chatSource)
        this.chatSource = null
      }
      if (this.greetingSource) {
        this.validSources.delete(this.greetingSource)
        this.greetingSource = null
      }
    } catch (error) {
      console.warn('[MessageSourceRegistry] Error clearing sources:', error)
    }
  }
}
